import maplibregl from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PathLayer, TextLayer } from '@deck.gl/layers';
import { distance } from '@turf/turf';
import { point } from '@turf/helpers';
import * as c from "../common.js";

let _distToolMarkers = [];
let _distToolOverlay = null;
let _distToolClickHandler = null;
let _distToolPoints = [];

export function addInteractiveDistanceTool(map) {
  c.setUIState("TOOLS", c.UI_STATE.TOOLS.WAITING_FOR_USER_CLICK);
  
  if (!_distToolOverlay) {
    _distToolOverlay = new MapboxOverlay({ layers: [] });
    map.addControl(_distToolOverlay);
  }
  
  let clickCount = 0;
  
  _distToolClickHandler = (e) => {
    if (clickCount >= 2) return;
    
    const coord = [e.lngLat.lng, e.lngLat.lat];
    _distToolPoints.push(coord);
    
    const marker = new maplibregl.Marker({ color: 'red', draggable: true })
      .setLngLat(coord)
      .addTo(map);
    
    _distToolMarkers.push(marker);
    
    marker.on('drag', () => {
      _distToolPoints[_distToolMarkers.indexOf(marker)] = [marker.getLngLat().lng, marker.getLngLat().lat];
      updateDistanceLine();
    });
    
    clickCount++;
    
    if (clickCount === 2) {
      updateDistanceLine();
      setTimeout(() => c.setUIState("TOOLS", c.UI_STATE.TOOLS.INACTIVE), 300);
    }
  };
  
  map.on('click', _distToolClickHandler);
}

function updateDistanceLine() {
  if (_distToolPoints.length !== 2 || !_distToolOverlay) return;
  
  const distanceMeters = distance(point(_distToolPoints[0]), point(_distToolPoints[1]), { units: 'meters' });
  const midpoint = [
    (_distToolPoints[0][0] + _distToolPoints[1][0]) / 2,
    (_distToolPoints[0][1] + _distToolPoints[1][1]) / 2
  ];
  
  _distToolOverlay.setProps({
    layers: [
      new PathLayer({
        id: 'distance-tool-line',
        data: [{ path: _distToolPoints }],
        getPath: d => d.path,
        getColor: [0, 0, 255, 255],
        getWidth: 2,
        widthMinPixels: 2
      }),
      new TextLayer({
        id: 'distance-tool-label',
        data: [{ position: midpoint, text: `${distanceMeters.toFixed(0)} m` }],
        getPosition: d => d.position,
        getText: d => d.text,
        getSize: 14,
        getColor: [0, 0, 0, 255],
        outlineColor: [255, 255, 255, 255],
        outlineWidth: 3,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center'
      })
    ]
  });
}

export function removeInteractiveDistanceTool(map) {
  _distToolMarkers.forEach(marker => marker.remove());
  _distToolMarkers = [];
  _distToolPoints = [];
  
  if (_distToolOverlay) {
    map.removeControl(_distToolOverlay);
    _distToolOverlay = null;
  }
  
  if (_distToolClickHandler) {
    map.off('click', _distToolClickHandler);
    _distToolClickHandler = null;
  }
}

let _wayPointMarker = null;
let _wayPointCoord = null;
let _wayPointClickHandler = null;

export function isWayPointToolRunning() {
  return _wayPointMarker != null;
}

export function getWayPointCoord() {
  return _wayPointCoord;
}

export function addInteractiveWayPointTool(map) {
  c.setUIState("TOOLS", c.UI_STATE.TOOLS.WAITING_FOR_USER_CLICK);
  
  _wayPointClickHandler = (e) => {
    if (_wayPointCoord !== null) return;
    
    _wayPointCoord = [e.lngLat.lng, e.lngLat.lat];
    
    _wayPointMarker = new maplibregl.Marker({ color: 'red', draggable: true })
      .setLngLat(_wayPointCoord)
      .addTo(map);
    
    _wayPointMarker.on('drag', () => {
      _wayPointCoord = [_wayPointMarker.getLngLat().lng, _wayPointMarker.getLngLat().lat];
      if (_wayPointDragCallback) _wayPointDragCallback();
    });
    
    if (_wayPointDragCallback) _wayPointDragCallback();
    
    setTimeout(() => c.setUIState("TOOLS", c.UI_STATE.TOOLS.INACTIVE), 300);
  };
  
  map.on('click', _wayPointClickHandler);
}

let _wayPointDragCallback = null;

export function setWayPointDragCallback(callback) {
  _wayPointDragCallback = callback;
}

export function removeInteractiveWayPointTool(map) {
  if (_wayPointMarker) {
    _wayPointMarker.remove();
    _wayPointMarker = null;
  }
  
  _wayPointCoord = null;
  
  if (_wayPointClickHandler) {
    map.off('click', _wayPointClickHandler);
    _wayPointClickHandler = null;
  }
}
